import { Editor } from '@tiptap/core';
import { isApiError } from '@wix/da-http-client';
import { PapiDeviation } from '@wix/da-papi-types';
import { requestPuppyFromExtension } from '../../utils/requestPuppyFromExtension';
import { getExternalRenderer } from '../../../components/utils/tiptap';

export async function fetchFreshDeviations({
  editor,
  deviations,
}: {
  editor: Editor;
  deviations: PapiDeviation[];
}) {
  const deviationMediaService = getExternalRenderer(editor, 'mediaService');

  const commissionIds: number[] = [];
  const deviationIds = deviations
    .slice(0, 24)
    .map(d => {
      if (deviationMediaService.getDeviationType(d) === 'commission') {
        commissionIds.push(d.deviationId);
        return undefined;
      }
      return d.deviationId;
    })
    .filter(i => i !== undefined);

  let devResult;
  let commResult;

  if (deviationIds.length > 0) {
    devResult = await requestPuppyFromExtension(
      editor,
      {
        method: 'get',
        url: `/deviation/multifetch`,
        params: {
          deviationids: deviationIds,
        },
      },
      () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line global-require
          const { deviationsByType } = require('@wix/da-sample-data');
          return { deviations: deviationsByType.image.slice(0, 10) };
        }
      }
    );

    if (isApiError(devResult)) {
      return devResult;
    }
  }

  if (commissionIds.length > 0) {
    commResult = await requestPuppyFromExtension(
      editor,
      {
        method: 'get',
        url: `/commission/multifetch`,
        params: {
          deviationids: deviationIds,
        },
      },
      () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line global-require
          const { deviationsByType } = require('@wix/da-sample-data');
          return { commissions: deviationsByType.commissions.slice(0, 10) };
        }
      }
    );

    if (isApiError(commResult)) {
      return commResult;
    }
  }

  const hasDeviations = (devResult?.deviations.length ?? 0) > 0;
  const hasCommissions = (commResult?.commissions.length ?? 0) > 0;

  // Merge results
  if (hasDeviations && hasCommissions) {
    const merged: PapiDeviation[] = [
      ...devResult.deviations,
      ...commResult.commissions,
    ];
    return deviations.map(d => {
      const fresh = merged.find(
        i => i.deviationId === d.deviationId && i.typeId === d.typeId
      );
      return fresh ?? d;
    });
  }

  // Only devs
  if (hasDeviations && !hasCommissions) {
    return devResult.deviations;
  }

  // Only commissions
  if (!hasDeviations && hasCommissions) {
    return commResult.commissions;
  }

  if (!hasDeviations && !hasCommissions) {
    return {
      error: 'Failed to fetch fresh data, maybe the deviation was deleted?',
    };
  }
}
